// ABOUTME: Pure reorder logic for dashboard drag-and-drop.
// ABOUTME: Extracted from StartDashboard so the persistence contract can be unit tested.

import type { DashboardItem } from "../services/dashboardService";

export interface ReorderRequest {
  sourceCol: number;
  destCol: number;
  /** Index within the destination column's order-sorted, rendered list. */
  destIndex: number;
  draggableId: string;
}

/**
 * Produces the next full dashboard item list for a drag operation.
 *
 * `items` MUST be the complete persistable set — never a search-filtered or
 * otherwise narrowed view. The result of this function is written to Firestore
 * with a whole-document replace, so anything missing from the input is deleted.
 *
 * Returns `null` when the drag is a no-op or cannot be resolved, so callers can
 * skip the write entirely.
 */
export function reorderDashboardItems(
  items: DashboardItem[],
  { sourceCol, destCol, destIndex, draggableId }: ReorderRequest
): DashboardItem[] | null {
  if (Number.isNaN(sourceCol) || Number.isNaN(destCol)) return null;

  const moving = items.find((i) => i.id === draggableId);
  // Ads are display-only injections and must never be persisted.
  if (!moving || moving.isAd) return null;

  const rest = items.filter((i) => i.id !== draggableId);

  // Rebuild the destination column from the same order-sorted projection the
  // renderer uses, so destIndex means the same thing here as it does on screen.
  const destColumn = rest.filter((i) => i.col === destCol).sort((a, b) => a.order - b.order);
  destColumn.splice(Math.min(Math.max(destIndex, 0), destColumn.length), 0, moving);

  const sourceColumn =
    sourceCol === destCol
      ? []
      : rest.filter((i) => i.col === sourceCol).sort((a, b) => a.order - b.order);

  const touched = new Set([sourceCol, destCol]);

  return [
    // Untouched columns — and any item whose col is out of range — pass through
    // unchanged rather than being dropped.
    ...rest.filter((i) => !touched.has(i.col)),
    ...destColumn.map((item, idx) => ({ ...item, col: destCol, order: idx })),
    ...sourceColumn.map((item, idx) => ({ ...item, col: sourceCol, order: idx })),
  ];
}
